import { readSession } from "./session.js";
import { refreshOrgToken } from "./oidc.js";
export const MEMBERS = ["commerce", "rnd", "scm", "mfg", "platform"];
export class ApiError extends Error {
    problem;
    status;
    constructor(status, problem) {
        super(problem.detail || problem.title || `HTTP ${status}`);
        this.name = "ApiError";
        this.status = status;
        this.problem = problem;
    }
    /** Human, short, safe to put in a notice. */
    get summary() {
        const bits = [this.problem.title || `HTTP ${this.status}`];
        if (this.problem.code)
            bits.push(`(${this.problem.code})`);
        const head = bits.join(" ");
        return this.problem.detail ? `${head} — ${this.problem.detail}` : head;
    }
}
export function newIdempotencyKey() {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === "function")
        return c.randomUUID();
    // Deterministic-shape fallback for non-secure contexts (older Safari).
    const rnd = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, "0");
    return `${rnd()}-${rnd().slice(0, 4)}-4${rnd().slice(0, 3)}-8${rnd().slice(0, 3)}-${rnd()}${rnd().slice(0, 4)}`;
}
/**
 * The Cloudflare Pages console (vnmade.xbuyer.com) and the edge gateway
 * (vnmade-api.xbuyer.com) are DIFFERENT origins. A relative /api path would be
 * swallowed by the Pages SPA fallback and return index.html (verified: 200
 * text/html, no API data). CI injects VITE_API_BASE_URL at build time; the CORS
 * allow-list on the Worker permits this exact origin.
 *
 * Leaving it empty preserves local development, where the Vite dev server
 * proxies /api to the gateway on the same origin (no CORS involved).
 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
function buildUrl(member, path, query) {
    const base = `${API_BASE}/api/v1/${member}${path.startsWith("/") ? path : `/${path}`}`;
    if (!query)
        return base;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null || v === "")
            continue;
        qs.set(k, String(v));
    }
    const s = qs.toString();
    return s ? `${base}?${s}` : base;
}
async function request(member, method, path, opts = {}) {
    const session = opts.session ?? readSession();
    if (!session) {
        throw new ApiError(401, {
            title: "Not signed in",
            status: 401,
            detail: "Open /login and sign in before calling the gateway.",
            code: "no_session",
        });
    }
    // Compute the key ONCE: a 401 -> refresh -> retry must reuse it, otherwise the
    // mutation would be executed twice under two different idempotency keys.
    const idempotencyKey = opts.idempotencyKey ?? newIdempotencyKey();
    const url = buildUrl(member, path, opts.query);
    const send = async (token) => {
        const headers = {
            authorization: `Bearer ${token}`,
            accept: "application/json",
            "x-vnmade-client": "vnmade-web/0.1.0",
        };
        if (method !== "GET")
            headers["idempotency-key"] = idempotencyKey;
        if (opts.body !== undefined)
            headers["content-type"] = "application/json";
        try {
            return await fetch(url, {
                method,
                headers,
                body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
                signal: opts.signal,
            });
        }
        catch (e) {
            if (e?.name === "AbortError")
                throw e;
            throw new ApiError(0, {
                title: "Gateway unreachable",
                status: 0,
                detail: "The API Gateway is not answering. Check VITE_API_BASE_URL / the Vite proxy target.",
                code: "network_error",
            });
        }
    };
    let res = await send(session.token);
    // Only OIDC sessions can self-heal: dev tokens are static, and an explicitly
    // supplied session belongs to the caller (tests / probes) and must not mutate.
    if (res.status === 401 && session.mode === "oidc" && !opts.session) {
        const next = await refreshOrgToken(session);
        if (next)
            res = await send(next.token);
    }
    if (res.status === 204)
        return undefined;
    const text = await res.text();
    let payload = null;
    if (text) {
        try {
            payload = JSON.parse(text);
        }
        catch {
            payload = { title: res.statusText || "Unexpected response", detail: text.slice(0, 400) };
        }
    }
    if (!res.ok) {
        const problem = payload && typeof payload === "object" ? payload : { title: res.statusText };
        if (res.status === 401) {
            problem.detail = "会话已过期或令牌无效，请重新使用 Logto 登录。" + (problem.detail ? ` (${problem.detail})` : "");
        }
        throw new ApiError(res.status, { status: problem.status ?? res.status, ...problem });
    }
    return payload;
}
export const api = {
    get: (m, p, o) => request(m, "GET", p, o),
    post: (m, p, o) => request(m, "POST", p, o),
    put: (m, p, o) => request(m, "PUT", p, o),
    patch: (m, p, o) => request(m, "PATCH", p, o),
    del: (m, p, o) => request(m, "DELETE", p, o),
    list: (m, p, o) => request(m, "GET", p, o),
};
/* ------------------------------------------------------------------ helpers */
export const money = (v, currency = "USD") => {
    if (v === null || v === undefined || v === "")
        return "—";
    const n = typeof v === "number" ? v : Number(String(v));
    if (!Number.isFinite(n))
        return String(v);
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(n);
    }
    catch {
        return `${n.toFixed(2)} ${currency}`;
    }
};
export const num = (v, digits = 0) => {
    if (v === null || v === undefined || v === "")
        return "—";
    const n = Number(String(v));
    if (!Number.isFinite(n))
        return String(v);
    return n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
};
export const dateTime = (v) => {
    if (!v)
        return "—";
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime()))
        return String(v);
    return d.toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};
export const dateOnly = (v) => {
    if (!v)
        return "—";
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime()))
        return String(v);
    return d.toLocaleDateString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" });
};
export const shortId = (v, head = 8) => {
    const s = String(v ?? "");
    return s.length > head + 3 ? `${s.slice(0, head)}…` : s || "—";
};
export const humanBytes = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0)
        return "—";
    const u = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    let x = n;
    while (x >= 1024 && i < u.length - 1) {
        x /= 1024;
        i++;
    }
    return `${x.toFixed(x >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};
/** Age in hours/days, for outbox / dead-letter triage. */
export const age = (v) => {
    if (!v)
        return "—";
    const ms = Date.now() - new Date(String(v)).getTime();
    if (!Number.isFinite(ms) || ms < 0)
        return "—";
    const m = Math.floor(ms / 60000);
    if (m < 1)
        return "刚刚";
    if (m < 60)
        return `${m} 分钟`;
    const h = Math.floor(m / 60);
    if (h < 24)
        return `${h} 小时`;
    return `${Math.floor(h / 24)} 天`;
};
